import * as React from 'react';

import {
  Alert,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { UIMessage } from 'ai';
import { Download, Eye, FolderOpen, Trash2 } from 'lucide-react';

import {
  deleteArtifact,
  downloadArtifact,
  isArtifactNotFound,
  isArtifactsUnavailable,
  listArtifacts,
} from '@modules/_shared/components/chat/artifactApi';
import {
  type ArtifactSummary,
  formatFileSize,
  previewKindOf,
} from '@modules/_shared/components/chat/artifactFiles';
import {
  chatApiBase,
  chatFetch,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';
import { useArtifactPreview } from '@modules/_shared/components/chat/useArtifactPreview';
import { formatStrictRelativeTime } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

type Scope = 'chat' | 'all';

type ListState =
  | { kind: 'loading' }
  | { kind: 'ready'; artifacts: Array<ArtifactSummary> }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string };

type ChatFilesProps = {
  // The open chat once it exists on the server; null for a new chat.
  activeChatId: string | null;
  onSelectChat: (
    chatId: string,
    messages: Array<{
      id: string;
      role: 'user' | 'assistant';
      parts: UIMessage['parts'];
      metadata?: unknown;
    }>,
    model?: string | null,
  ) => void;
  // False while a reply streams: opening another chat would halt it.
  canSwitchChat: boolean;
  // Changes when a tool result arrives (chatPanelViews.settledToolResultCount),
  // so files saved during the current reply show up.
  refreshKey: number;
  // The chat view has an approval or an email waiting (dot on Back).
  chatWaitsForUser: boolean;
  onBack: () => void;
};

// The user's files saved by Aura in the current agency (the chat API scopes
// them to the JWT's user + tenant). Download and preview ask for a fresh
// signed URL each time; a 404 means the file is gone (deleted in another tab)
// and removes the row.
export const ChatFiles = ({
  activeChatId,
  onSelectChat,
  canSwitchChat,
  refreshKey,
  chatWaitsForUser,
  onBack,
}: ChatFilesProps) => {
  const { t, dateLocale, intlLocale } = useLocale();
  const { colors } = useTheme();
  const [scope, setScope] = React.useState<Scope>(
    activeChatId != null ? 'chat' : 'all',
  );
  const [list, setList] = React.useState<ListState>({ kind: 'loading' });
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [toDelete, setToDelete] = React.useState<ArtifactSummary | null>(null);
  const [loadingChatId, setLoadingChatId] = React.useState<string | null>(null);

  const scopeChatId = scope === 'chat' ? activeChatId : null;
  // A refetch for the same scope keeps the rows on screen instead of a spinner.
  const listedScopeRef = React.useRef<string | null | undefined>(undefined);
  const openAbortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => () => openAbortRef.current?.abort(), []);

  React.useEffect(() => {
    const controller = new AbortController();
    if (listedScopeRef.current !== scopeChatId) {
      setList({ kind: 'loading' });
    }
    listArtifacts({
      chatId: scopeChatId ?? undefined,
      signal: controller.signal,
    })
      .then(artifacts => {
        if (!controller.signal.aborted) {
          listedScopeRef.current = scopeChatId;
          setList({ kind: 'ready', artifacts });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setList(
          isArtifactsUnavailable(error)
            ? { kind: 'unavailable' }
            : { kind: 'error', message: t('Failed to load your files') },
        );
      });
    return () => controller.abort();
  }, [scopeChatId, refreshKey, t]);

  const removeRow = (id: string) =>
    setList(prev =>
      prev.kind === 'ready'
        ? { ...prev, artifacts: prev.artifacts.filter(a => a.id !== id) }
        : prev,
    );

  const reportFailure = (
    artifact: ArtifactSummary,
    error: unknown,
    message: string,
  ) => {
    if (isArtifactNotFound(error)) {
      removeRow(artifact.id);
      setActionError(
        t('{{name}} no longer exists and was removed from the list.', {
          name: artifact.name,
        }),
      );
      return;
    }
    setActionError(message);
  };

  const runAction = async (
    artifact: ArtifactSummary,
    action: () => Promise<void>,
    failureMessage: string,
  ) => {
    setBusyId(artifact.id);
    setActionError(null);
    try {
      await action();
    } catch (error) {
      reportFailure(artifact, error, failureMessage);
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = (artifact: ArtifactSummary) =>
    runAction(
      artifact,
      () => downloadArtifact(artifact.id),
      t('Failed to download the file'),
    );

  const previews = useArtifactPreview({
    onGone: artifact => removeRow(artifact.id),
    onFailure: (artifact, error) => {
      if (error == null) {
        setActionError(t('Failed to load the preview'));
      } else {
        reportFailure(artifact, error, t('Failed to load the preview'));
      }
    },
    onDownload: artifact => void handleDownload(artifact),
  });

  // HTML failures are reported by the preview itself (in its dialog); an
  // image or a PDF whose URL can't be signed fails like any other action.
  const handlePreview = (artifact: ArtifactSummary) =>
    previewKindOf(artifact) === 'html'
      ? previews.preview(artifact)
      : runAction(
          artifact,
          () => previews.preview(artifact),
          t('Failed to load the preview'),
        );

  const handleDelete = async () => {
    const target = toDelete;
    if (target == null) {
      return;
    }
    setToDelete(null);
    setBusyId(target.id);
    setActionError(null);
    try {
      await deleteArtifact(target.id);
      removeRow(target.id);
    } catch (error) {
      // Already gone (another tab): the row goes either way.
      if (isArtifactNotFound(error)) {
        removeRow(target.id);
      } else {
        setActionError(t('Failed to delete the file'));
      }
    } finally {
      setBusyId(null);
    }
  };

  // Mirror ChatHistory.handleSelect: Back (which unmounts this view) or
  // another open aborts the load, so a late answer never switches the chat
  // and halts a reply started meanwhile.
  const openChat = async (chatId: string) => {
    if (chatId === activeChatId) {
      onBack();
      return;
    }
    if (!canSwitchChat) {
      return;
    }
    openAbortRef.current?.abort();
    const controller = new AbortController();
    openAbortRef.current = controller;
    setLoadingChatId(chatId);
    try {
      const res = await chatFetch(`${chatApiBase}/${chatId}`, {
        headers: getChatHeaders(),
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!controller.signal.aborted) {
        onSelectChat(chatId, data.messages ?? [], data.model ?? null);
      }
    } catch (err) {
      if (!(err instanceof Error && err.name === 'AbortError')) {
        setActionError(t('Failed to load chat. Please try again.'));
      }
    } finally {
      if (openAbortRef.current === controller) {
        setLoadingChatId(null);
      }
    }
  };

  const perAgencyNote = t(
    'Files are per agency: you only see the ones saved in this agency.',
  );

  const renderBody = () => {
    switch (list.kind) {
      case 'loading':
        return (
          <Stack sx={{ alignItems: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        );
      case 'unavailable':
        return (
          <EmptyState
            title={t('Files are not available in this environment yet.')}
          />
        );
      case 'error':
        return <EmptyState title={list.message} />;
      case 'ready':
        if (list.artifacts.length === 0) {
          return (
            <EmptyState
              title={
                scope === 'chat'
                  ? t('No files saved in this chat yet.')
                  : t(
                      'No files yet. Ask Aura to save a report or an export and it will appear here.',
                    )
              }
              note={perAgencyNote}
            />
          );
        }
        return (
          <List disablePadding>
            {list.artifacts.map(artifact => {
              const busy = busyId === artifact.id;
              const canPreview = previewKindOf(artifact) != null;
              const { chatId } = artifact;
              return (
                <ListItem
                  key={artifact.id}
                  sx={{
                    py: 1,
                    px: 1.5,
                    gap: 0.5,
                    borderBottom: `1px solid ${colors.divider}`,
                  }}
                >
                  <ListItemText
                    primary={artifact.name}
                    secondary={
                      <>
                        {formatFileSize(artifact.sizeBytes, intlLocale)}
                        {' · '}
                        {formatStrictRelativeTime(
                          artifact.createdAt,
                          dateLocale,
                        )}
                        {chatId != null && (
                          <>
                            {' · '}
                            {loadingChatId === chatId ? (
                              <CircularProgress size={10} />
                            ) : (
                              <Link
                                component="button"
                                type="button"
                                onClick={() => void openChat(chatId)}
                                disabled={
                                  !canSwitchChat && chatId !== activeChatId
                                }
                                sx={{ fontSize: 'inherit' }}
                              >
                                {chatId === activeChatId
                                  ? t('This chat')
                                  : t('Open the chat')}
                              </Link>
                            )}
                          </>
                        )}
                      </>
                    }
                    slotProps={{
                      primary: {
                        noWrap: true,
                        title: artifact.name,
                        sx: { fontSize: '0.8125rem' },
                      },
                      secondary: { sx: { fontSize: '0.6875rem' } },
                    }}
                  />
                  {busy ? (
                    <CircularProgress size={14} sx={{ flexShrink: 0 }} />
                  ) : (
                    <Stack direction="row" sx={{ flexShrink: 0 }}>
                      {canPreview && (
                        <IconButton
                          size="small"
                          onClick={() => void handlePreview(artifact)}
                          title={t('Preview')}
                          aria-label={t('Preview')}
                        >
                          <Eye size={15} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => void handleDownload(artifact)}
                        title={t('Download')}
                        aria-label={t('Download')}
                      >
                        <Download size={15} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setToDelete(artifact)}
                        title={t('Delete')}
                        aria-label={t('Delete')}
                      >
                        <Trash2 size={15} />
                      </IconButton>
                    </Stack>
                  )}
                </ListItem>
              );
            })}
          </List>
        );
    }
  };

  return (
    <div css={{ flex: 1, overflowY: 'auto' }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${colors.divider}`,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_, value: Scope | null) => {
            if (value != null) {
              setScope(value);
            }
          }}
          aria-label={t('Files')}
        >
          <ToggleButton
            value="chat"
            disabled={activeChatId == null}
            sx={{ textTransform: 'none', py: 0.25 }}
          >
            {t('This chat')}
          </ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', py: 0.25 }}>
            {t('All')}
          </ToggleButton>
        </ToggleButtonGroup>
        <Badge color="primary" variant="dot" invisible={!chatWaitsForUser}>
          <Button onClick={onBack} size="small" variant="text">
            {t('Back')}
          </Button>
        </Badge>
      </Stack>
      {actionError != null && (
        <Alert
          severity="error"
          onClose={() => setActionError(null)}
          sx={{ m: 1, borderRadius: 1 }}
        >
          {actionError}
        </Alert>
      )}
      {renderBody()}
      <Dialog open={toDelete != null} onClose={() => setToDelete(null)}>
        <DialogContent>
          <DialogContentText>
            {t('Delete {{name}}? This cannot be undone.', {
              name: toDelete?.name ?? '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>{t('Cancel')}</Button>
          <Button
            onClick={() => void handleDelete()}
            color="error"
            startIcon={<Trash2 />}
            autoFocus
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      {previews.dialogs}
    </div>
  );
};

const EmptyState = ({ title, note }: { title: string; note?: string }) => {
  const { colors } = useTheme();
  return (
    <Stack
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.grey400,
        textAlign: 'center',
        p: 4,
        gap: 1,
      }}
    >
      <FolderOpen size={32} />
      <Typography variant="body2" sx={{ color: colors.grey500 }}>
        {title}
      </Typography>
      {note != null && (
        <Typography variant="caption" sx={{ color: colors.grey500 }}>
          {note}
        </Typography>
      )}
    </Stack>
  );
};
